// MANISH CUSTOM CODE FOR SCENTED STORIES - START
//
// Video behaviour:
//   - Only videos that are actually visible within the horizontally
//     scrolling row (or about to scroll into view) load and play. Videos
//     further along the row - off-screen until the visitor scrolls to
//     them - stay paused/unloaded until they're actually needed.
//   - Video audio unmutes automatically EVERY time the section enters the
//     page viewport - not just the first time.
//   - Section leaves the page viewport -> all videos muted + paused
//   - Section re-enters the page viewport -> currently row-visible videos
//     resume playing AND the active one unmutes again
//   - Mute button: only one video audible at a time; whichever one the
//     visitor last unmuted is remembered as the "active" video, so
//     scrolling away and back restores that same video's audio instead of
//     always resetting to the first one.
//   - Tab hidden -> muted + paused; tab shown -> resume (and unmute, if the
//     section is currently in view)
//
// THIS UPDATE - FIXES SPECIFIC VIDEOS STUCK ON THE POSTER FRAME, NEVER
// PLAYING: safePlay() previously only did two things - check readyState,
// then either call .play() immediately or wait for a 'canplay' event that
// might just never fire. With `preload="metadata"` (needed for the
// carousel loading fix), some browsers - Safari/iOS in particular - won't
// actually start fetching real video data until `.load()` is explicitly
// called, even though `.play()` was already requested. If that never
// happens, 'canplay' never fires, and the video sits frozen on its
// poster image forever with no error and no retry - exactly the
// "specific videos never start playing" symptom.
//
// safePlay() now:
//   1. Explicitly calls video.load() first if the browser hasn't started
//      loading yet (readyState === 0 / HAVE_NOTHING) - forces the network
//      request to actually begin, closing the Safari/iOS gap above.
//   2. Listens for both 'canplay' AND 'loadeddata' (whichever fires
//      first) instead of only 'canplay' - some browsers are more
//      consistent about firing one over the other.
//   3. Has a 4-second safety timeout: if neither event has fired by
//      then, it calls video.load() again and retries once more - a
//      single self-heal for a stalled/dropped initial request, rather
//      than leaving that card permanently stuck.
//   4. Listens for the video's 'error' event and retries once via the
//      same load()+play() path, in case of a transient network error
//      rather than a genuinely broken source file.
//
// THIS UPDATE (earlier) - SMOOTHER LOADING, NO GLITCHES: the previous
// version called playAll() unconditionally on page load, which told
// EVERY video card in the row to start downloading and playing
// immediately - including every card sitting off-screen in this
// multi-card horizontal carousel, not just the ones actually visible.
// Combined with `preload="auto"` on every video (see
// mm-scented-stories.liquid), that meant every video on the page was
// competing for bandwidth at once on load, which is exactly what made
// loading feel slow and glitchy - including for the videos the visitor
// could actually see.
//
// Fixed with a second IntersectionObserver scoped specifically to the
// horizontally-scrolling row (`root: row`), watching each video card
// individually. A video now only starts playing once its own card is
// actually visible within the row (or about to be, via a rootMargin that
// starts loading slightly before it comes fully into view) - and it's
// paused again the moment it scrolls back out. This is layered on top of
// the existing page-viewport observer: a video only ever plays when BOTH
// conditions are true - the whole section is on screen AND that specific
// card is scrolled into view within the row.
//
// THIS UPDATE (earlier still) fixed a bug: unmuting only ever happened
// ONCE per page load. A `firstUnmuteApplied` flag was set to true the
// first time the section entered the viewport and was never reset, so on
// every later re-entry the `if (!firstUnmuteApplied)` check silently
// skipped the unmute call forever - the video kept playing, just
// permanently muted after that first time. Replaced with an
// `activePair` reference (defaulting to the first video) that gets
// unmuted on every single viewport entry, and gets updated whenever the
// visitor picks a different video's mute button, so repeat entries stay
// in sync with whichever video they'd chosen.

function initScentedStories(section) {
  if (section.dataset.mmStoriesInit) return;
  section.dataset.mmStoriesInit = 'true';

  // ── SCROLL NAV ────────────────────────────────────────────────────────────
  const row = section.querySelector('[data-mm-cards-row]');
  const prevBtn = section.querySelector('.mm-scented-stories__nav--prev');
  const nextBtn = section.querySelector('.mm-scented-stories__nav--next');

  function getScrollAmount() {
    const firstItem = row && row.querySelector('.mm-scented-stories__item');
    if (!firstItem) return 280;
    return firstItem.offsetWidth + parseInt(getComputedStyle(row).gap || '14', 10);
  }

  function updateNavState() {
    if (!row || !prevBtn || !nextBtn) return;
    prevBtn.disabled = row.scrollLeft <= 4;
    nextBtn.disabled = row.scrollLeft + row.offsetWidth >= row.scrollWidth - 4;
  }

  if (row) row.addEventListener('scroll', updateNavState, { passive: true });
  if (prevBtn) prevBtn.addEventListener('click', () => row && row.scrollBy({ left: -getScrollAmount(), behavior: 'smooth' }));
  if (nextBtn) nextBtn.addEventListener('click', () => row && row.scrollBy({ left: getScrollAmount(), behavior: 'smooth' }));
  updateNavState();

  // ── VIDEO PAIRS ───────────────────────────────────────────────────────────
  const videoWraps = Array.from(section.querySelectorAll('[data-mm-story-video-wrap]'));
  if (videoWraps.length === 0) return;

  const videoPairs = videoWraps
    .map((wrap) => ({ wrap, video: wrap.querySelector('video'), btn: wrap.querySelector('[data-mm-story-mute-btn]') }))
    .filter((p) => p.video);

  if (videoPairs.length === 0) return;

  let sectionInView = false;
  // Which video cards are currently scrolled into view within the row -
  // only these are candidates to actually play, regardless of whether the
  // section itself is on screen.
  const rowVisible = new Set();
  // Which video should be unmuted whenever the section is in view - starts
  // as the first video, and updates whenever the visitor clicks a
  // different video's mute button.
  let activePair = videoPairs[0];

  // ── HELPERS ───────────────────────────────────────────────────────────────
  function setUnmuted(pair) {
    pair.video.muted = false;
    if (pair.btn) {
      pair.btn.setAttribute('aria-pressed', 'true');
      pair.btn.setAttribute('aria-label', 'Mute video');
    }
  }

  function setMuted(pair) {
    pair.video.muted = true;
    if (pair.btn) {
      pair.btn.setAttribute('aria-pressed', 'false');
      pair.btn.setAttribute('aria-label', 'Unmute video');
    }
  }

  function muteAll() { videoPairs.forEach(setMuted); }
  function pauseAll() { videoPairs.forEach((p) => p.video.pause()); }

  // Tracks in-flight retry timers per video so a second call to safePlay
  // for the same video (e.g. re-entering the row/viewport quickly)
  // doesn't stack up duplicate retry timers.
  const retryTimers = new WeakMap();

  // Plays a single video robustly:
  //  - forces the browser to actually start fetching data (some browsers,
  //    Safari/iOS especially, won't with only preload="metadata" set,
  //    even after .play() is called)
  //  - waits for whichever of 'canplay' / 'loadeddata' fires first
  //  - if neither fires within 4s (stalled/dropped request), retries once
  //    with an explicit reload
  //  - retries once on a genuine 'error' event too
  function safePlay(video) {
    function attempt() {
      const p = video.play();
      if (p) p.catch(() => {});
    }

    if (video.readyState >= 3) {
      // Already has enough data buffered - play immediately, no need to wait.
      attempt();
      return;
    }

    // Clear any previous pending retry for this video before starting a new one.
    const existingTimer = retryTimers.get(video);
    if (existingTimer) {
      window.clearTimeout(existingTimer.timeoutId);
      video.removeEventListener('canplay', existingTimer.onReady);
      video.removeEventListener('loadeddata', existingTimer.onReady);
      video.removeEventListener('error', existingTimer.onError);
    }

    let settled = false;

    function onReady() {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      video.removeEventListener('canplay', onReady);
      video.removeEventListener('loadeddata', onReady);
      video.removeEventListener('error', onError);
      retryTimers.delete(video);
      attempt();
    }

    function onError() {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      video.removeEventListener('canplay', onReady);
      video.removeEventListener('loadeddata', onReady);
      video.removeEventListener('error', onError);
      retryTimers.delete(video);
      // One retry on a genuine load error, in case it was transient.
      video.load();
      video.addEventListener('canplay', attempt, { once: true });
    }

    video.addEventListener('canplay', onReady, { once: true });
    video.addEventListener('loadeddata', onReady, { once: true });
    video.addEventListener('error', onError, { once: true });

    const timeoutId = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      video.removeEventListener('canplay', onReady);
      video.removeEventListener('loadeddata', onReady);
      video.removeEventListener('error', onError);
      retryTimers.delete(video);
      // Nothing fired in time - the request likely never really started or
      // stalled. Force it to actually begin, then retry once more.
      video.load();
      video.addEventListener('canplay', attempt, { once: true });
    }, 4000);

    retryTimers.set(video, { timeoutId, onReady, onError });

    // Force the browser to actually start fetching data - with only
    // preload="metadata" set, some browsers won't begin the real network
    // request until .load() is explicitly called, even after .play().
    if (video.readyState === 0) {
      video.load();
    }
  }

  // Only plays cards that are BOTH currently row-visible (scrolled into
  // view within the horizontal carousel) - videos further along the row
  // are left alone until they actually scroll into view themselves.
  function playRowVisible() {
    videoPairs.forEach((p) => {
      if (rowVisible.has(p)) safePlay(p.video);
    });
  }

  // ── MUTE BUTTONS ──────────────────────────────────────────────────────────
  videoPairs.forEach((pair) => {
    if (!pair.btn) return;
    pair.btn.addEventListener('click', () => {
      if (!sectionInView) return; // no audio changes when off-screen
      const isUnmuted = pair.btn.getAttribute('aria-pressed') === 'true';
      if (isUnmuted) {
        setMuted(pair);
      } else {
        videoPairs.forEach((other) => { if (other !== pair) setMuted(other); });
        setUnmuted(pair);
        // Remember this as the video to auto-unmute on future viewport entries.
        activePair = pair;
      }
    });
  });

  // ── ROW (HORIZONTAL SCROLL) VISIBILITY ───────────────────────────────────
  // Scoped to `root: row` so it measures visibility WITHIN the scrollable
  // carousel, not the page viewport (that's the separate observer below).
  // A ~60% side rootMargin lets a card start loading slightly before it's
  // fully scrolled into view, so playback is ready by the time it arrives
  // instead of visibly starting up after the fact.
  const rowObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const pair = videoPairs.find((p) => p.wrap === entry.target);
        if (!pair) return;

        if (entry.isIntersecting) {
          rowVisible.add(pair);
          if (sectionInView) safePlay(pair.video);
        } else {
          rowVisible.delete(pair);
          pair.video.pause();
        }
      });
    },
    { root: row || null, rootMargin: '0px 60% 0px 60%', threshold: 0.01 }
  );

  videoWraps.forEach((wrap) => rowObserver.observe(wrap));

  // ── PAGE VIEWPORT VISIBILITY ─────────────────────────────────────────────
  const observerTarget = section.closest('.mm-section-bleed-wrapper') || section;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          sectionInView = true;
          // Resume only whichever cards are currently scrolled into view
          // within the row - not every video in the section.
          playRowVisible();
          // Unmute the active video EVERY time the section enters the
          // viewport, not just the first time.
          setUnmuted(activePair);
        } else {
          sectionInView = false;
          muteAll();
          pauseAll();
        }
      });
    },
    { threshold: 0.15 }
  );

  observer.observe(observerTarget);

  // ── PAGE VISIBILITY (BROWSER TAB) ─────────────────────────────────────────
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      muteAll();
      pauseAll();
    } else if (sectionInView) {
      playRowVisible();
      setUnmuted(activePair);
    }
  });
}

function initAll() {
  document.querySelectorAll('[data-mm-scented-stories]').forEach(initScentedStories);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAll);
} else {
  initAll();
}

document.addEventListener('shopify:section:load', initAll);

// MANISH CUSTOM CODE FOR SCENTED STORIES - END
